import { useState } from "react";
import { useLocation } from "wouter";
import type { AuthUser } from "../hooks/useAuth";
import { authFetch } from "@/lib/auth-fetch";
import { apiUrl } from "@/lib/api-base";

const JOURNALING_GOALS = [
  "Process what I'm learning",
  "Build self-awareness",
  "Set & track intentions",
  "Work through decisions",
  "Just explore freely",
];

interface OnboardingProps {
  user: AuthUser;
  onComplete: () => void;
}

export default function Onboarding({ user, onComplete }: OnboardingProps) {
  const [, setLocation] = useLocation();
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [name, setName] = useState(
    user.displayName || [user.firstName, user.lastName].filter(Boolean).join(" ") || "",
  );
  const [goal, setGoal] = useState("");
  const [customGoal, setCustomGoal] = useState("");
  const [saving, setSaving] = useState(false);

  const displayName = name.trim() || user.firstName || "there";

  const nameValid = name.trim().length > 0;
  const goalValid = !!(goal || customGoal.trim());
  const canContinue = nameValid && goalValid;

  async function handleFinish() {
    setSaving(true);
    await authFetch(apiUrl("/user/profile"), {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        displayName: name.trim() || undefined,
        journalingGoal: customGoal.trim() || goal || undefined,
        onboardingCompleted: true,
      }),
    });

    setSaving(false);
    onComplete();
    setLocation("/");
  }

  return (
    <div className="min-h-screen bg-[hsl(40_30%_97%)] flex flex-col items-center justify-center p-6">
      <div className="w-full max-w-lg">
        {/* Progress dots */}
        <div className="flex gap-2 justify-center mb-10">
          {[1, 2, 3].map((s) => (
            <div
              key={s}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                s <= step ? "w-8 bg-primary" : "w-4 bg-primary/20"
              }`}
            />
          ))}
        </div>

        {step === 1 && (
          <div className="text-center animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="mb-6">
              {user.profileImageUrl ? (
                <img
                  src={user.profileImageUrl}
                  alt="Your avatar"
                  className="w-20 h-20 rounded-full mx-auto mb-4 ring-4 ring-primary/20"
                />
              ) : (
                <div className="w-20 h-20 rounded-full bg-primary/15 mx-auto mb-4 flex items-center justify-center text-3xl font-display text-primary">
                  {(user.firstName?.[0] || user.email?.[0] || "?").toUpperCase()}
                </div>
              )}
            </div>
            <h1 className="font-display text-4xl font-semibold text-stone-800 mb-3">
              Welcome to Unfold{user.firstName ? `, ${user.firstName}` : ""}
            </h1>
            <p className="text-stone-500 text-lg leading-relaxed max-w-sm mx-auto mb-10">
              A quiet place to turn what you're learning into insights that actually stick.
            </p>
            <button
              onClick={() => setStep(2)}
              className="px-8 py-3.5 bg-primary text-white rounded-full font-sans font-medium hover:bg-[hsl(258_35%_43%)] transition-colors"
            >
              Let's begin
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
            <h2 className="font-display text-3xl text-stone-800 mb-2 text-center">
              What should we call you?
            </h2>
            <p className="text-stone-500 text-center mb-8">
              This will appear in your Unfoldings.
            </p>

            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
              maxLength={80}
              className="w-full px-5 py-4 rounded-2xl border border-stone-200 bg-white text-stone-800 placeholder-stone-400 text-lg font-sans focus:outline-none focus:ring-2 focus:ring-primary/30 mb-6"
              onKeyDown={(e) => e.key === "Enter" && canContinue && setStep(3)}
              autoFocus
            />

            <h3 className="font-sans font-medium text-stone-700 mb-3">
              What brings you to journaling?
            </h3>
            <div className="flex flex-col gap-2 mb-8">
              {JOURNALING_GOALS.map((g) => (
                <button
                  key={g}
                  onClick={() => {
                    setGoal(g);
                    setCustomGoal("");
                  }}
                  className={`px-5 py-3.5 rounded-xl text-left font-sans transition-colors border ${
                    goal === g
                      ? "bg-primary/10 border-primary/40 text-[hsl(258_35%_45%)]"
                      : "bg-white border-stone-200 text-stone-600 hover:border-stone-300"
                  }`}
                >
                  {g}
                </button>
              ))}
              <input
                type="text"
                value={customGoal}
                onChange={(e) => {
                  setCustomGoal(e.target.value);
                  setGoal("");
                }}
                placeholder="Or describe your own..."
                maxLength={120}
                className="px-5 py-3.5 rounded-xl border border-stone-200 bg-white text-stone-700 placeholder-stone-400 font-sans focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setStep(1)}
                className="px-6 py-3 rounded-full border border-stone-200 text-stone-500 font-sans hover:border-stone-300 transition-colors"
              >
                Back
              </button>
              <button
                onClick={() => setStep(3)}
                disabled={!canContinue}
                className="flex-1 px-6 py-3 bg-primary text-white rounded-full font-sans font-medium hover:bg-[hsl(258_35%_43%)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-primary"
              >
                Continue
              </button>
            </div>
            {!canContinue && (
              <p className="text-stone-400 text-sm text-center mt-4 font-sans">
                {!nameValid && !goalValid
                  ? "Enter your name and choose a goal to continue"
                  : !nameValid
                    ? "Enter your name to continue"
                    : "Choose a goal to continue"}
              </p>
            )}
          </div>
        )}

        {step === 3 && (
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
            <h2 className="font-display text-3xl text-stone-800 mb-2 text-center">
              You're all set, {displayName.split(" ")[0]}!
            </h2>
            <p className="text-stone-500 text-center mb-10 leading-relaxed">
              Your space is ready. Let's start unfolding.
            </p>

            <button
              onClick={() => handleFinish()}
              disabled={saving}
              className="w-full px-6 py-4 bg-primary text-white rounded-2xl font-sans font-medium text-lg hover:bg-[hsl(258_35%_43%)] transition-colors disabled:opacity-60"
            >
              {saving ? "Setting up…" : "Get started →"}
            </button>

            <div className="flex gap-3 mt-4 justify-center">
              <button
                onClick={() => setStep(2)}
                className="text-stone-400 text-sm font-sans hover:text-stone-600 transition-colors"
              >
                ← Back
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
